<div class="notif" [class.notif--dirty]="dirty">

  <a class="notif-back" routerLink="/main/project/{{ projectId }}"><i class="fa fa-angle-left" aria-hidden="true"></i> Project</a>

  <header class="notif-header">
    <div>
      <p class="notif-eyebrow" data-testid="notif-project-name">{{ projectName }}</p>
      <h1 class="notif-title">Notifications</h1>
      <p class="notif-subtitle">Choose who hears about outages, new errors and error spikes — and where.</p>
    </div>
    @if (form) {
      <button class="pw-btn pw-btn--ghost" type="button" (click)="sendTest()" [disabled]="testing || saving"
        data-testid="notif-send-test">
        <i class="fa" [class.fa-paper-plane]="!testing" [class.fa-spinner]="testing" [class.fa-spin]="testing"
          aria-hidden="true"></i>
        Send test alert
      </button>
    }
  </header>

  @if (loading) {
    <div class="n-card n-card--skeleton" data-testid="notif-loading">
      <div class="sk sk--title"></div>
      <div class="sk"></div>
      <div class="sk sk--short"></div>
    </div>
  } @else if (loadError) {
    <div class="n-card n-empty" data-testid="notif-load-error">
      <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
      <p>Couldn’t load the notification settings for this project.</p>
    </div>
  } @else if (form) {

    <div class="notif-channels">
      <!-- Email -->
      <section class="n-card" data-testid="notif-email-card">
        <div class="n-card__head">
          <span class="n-card__icon n-card__icon--email"><i class="fa fa-envelope" aria-hidden="true"></i></span>
          <div class="n-card__heading">
            <h2>Email</h2>
            <p>{{ form.email_enabled ? recipientCount + (recipientCount === 1 ? ' recipient' : ' recipients') : 'Off' }}</p>
          </div>
          <label class="n-switch">
            <input type="checkbox" [(ngModel)]="form.email_enabled" aria-label="Email alerts"
              data-testid="notif-email-enabled">
            <span class="n-switch__track"></span>
          </label>
        </div>

        <div class="n-card__body" [class.n-card__body--off]="!form.email_enabled">
          <h3 class="n-label">Team members</h3>
          <ul class="n-members">
            @for (member of members; track member.uuid) {
              <li>
                <label class="n-member" [class.n-member--on]="isRecipient(member)" data-testid="notif-member">
                  <input type="checkbox" [checked]="isRecipient(member)" (change)="toggleRecipient(member)"
                    [disabled]="!form.email_enabled">
                  <span class="n-member__avatar">{{ initials(member) }}</span>
                  <span class="n-member__text">
                    <span class="n-member__name">{{ member.name.trim() || member.email }}</span>
                    <span class="n-member__email">{{ member.email }}</span>
                  </span>
                  <i class="fa fa-check n-member__check" aria-hidden="true"></i>
                </label>
              </li>
            }
          </ul>

          <h3 class="n-label">Other addresses</h3>
          <div class="n-chips" [class.n-chips--error]="newEmailError">
            @for (email of form.extra_emails; track email) {
              <span class="n-chip" data-testid="notif-extra-email">
                {{ email }}
                <button type="button" (click)="removeEmail(email)" [attr.aria-label]="'Remove ' + email"
                  [disabled]="!form.email_enabled">
                  <i class="fa fa-times" aria-hidden="true"></i>
                </button>
              </span>
            }
            <input type="email" [(ngModel)]="newEmail" (keydown)="onEmailKeydown($event)" (blur)="addEmail()"
              (input)="newEmailError = ''" placeholder="oncall@company.com" aria-label="Add an email address"
              [disabled]="!form.email_enabled" data-testid="notif-extra-email-input">
          </div>
          @if (newEmailError) {
            <p class="n-error" data-testid="notif-extra-email-error">{{ newEmailError }}</p>
          } @else {
            <p class="n-hint">Press Enter to add. Useful for on-call aliases or people outside PulseWatch.</p>
          }
        </div>
      </section>

      <!-- Telegram -->
      <section class="n-card" data-testid="notif-telegram-card">
        <div class="n-card__head">
          <span class="n-card__icon n-card__icon--telegram"><i class="fa fa-paper-plane" aria-hidden="true"></i></span>
          <div class="n-card__heading">
            <h2>Telegram</h2>
            <p>
              @if (!telegramAvailable) {
                Not available
              } @else if (!form.telegram_enabled) {
                Off
              } @else {
                {{ chats.length }} {{ chats.length === 1 ? 'chat' : 'chats' }} connected
              }
            </p>
          </div>
          @if (telegramAvailable) {
            <label class="n-switch">
              <input type="checkbox" [(ngModel)]="form.telegram_enabled" aria-label="Telegram alerts"
                data-testid="notif-telegram-enabled">
              <span class="n-switch__track"></span>
            </label>
          }
        </div>

        <div class="n-card__body" [class.n-card__body--off]="telegramAvailable && !form.telegram_enabled">
          @if (!telegramAvailable) {
            <p class="n-hint n-hint--block" data-testid="notif-telegram-unavailable">
              Telegram alerts aren’t set up on this server yet. An administrator needs to configure the PulseWatch bot.
            </p>
          } @else {
            @if (chats.length) {
              <ul class="n-chats">
                @for (chat of chats; track chat.id) {
                  <li class="n-chat" data-testid="notif-telegram-chat">
                    <i class="fa" [class.fa-users]="chat.chat_type !== 'private'" [class.fa-user]="chat.chat_type === 'private'"
                      aria-hidden="true"></i>
                    <span class="n-chat__title">{{ chat.title || 'Telegram chat' }}</span>
                    <span class="n-chat__type">{{ chat.chat_type === 'private' ? 'Private' : 'Group' }}</span>
                    <button type="button" class="n-icon-btn" (click)="removeChat(chat)"
                      [attr.aria-label]="'Disconnect ' + (chat.title || 'chat')" title="Disconnect"
                      data-testid="notif-telegram-remove">
                      <i class="fa fa-trash-o" aria-hidden="true"></i>
                    </button>
                  </li>
                }
              </ul>
            }

            @if (telegramLink) {
              <div class="n-connect" data-testid="notif-telegram-connect-panel">
                <p class="n-connect__status"><span class="n-pulse"></span> Waiting for Telegram…</p>
                <p class="n-hint">Open the bot and press <b>Start</b>. For a group, pick the group and confirm.</p>
                <div class="n-connect__actions">
                  <a class="pw-btn pw-btn--telegram" [href]="telegramLink.private_url" target="_blank" rel="noopener"
                    data-testid="notif-telegram-private-link">
                    <i class="fa fa-user" aria-hidden="true"></i> Private chat
                  </a>
                  <a class="pw-btn pw-btn--ghost" [href]="telegramLink.group_url" target="_blank" rel="noopener"
                    data-testid="notif-telegram-group-link">
                    <i class="fa fa-users" aria-hidden="true"></i> Add to a group
                  </a>
                  <button type="button" class="n-link-btn" (click)="cancelTelegram()">Cancel</button>
                </div>
              </div>
            } @else {
              <button type="button" class="pw-btn pw-btn--ghost pw-btn--block" (click)="connectTelegram()"
                [disabled]="!form.telegram_enabled" data-testid="notif-telegram-connect">
                <i class="fa fa-plus" aria-hidden="true"></i> Connect a Telegram chat
              </button>
            }
          }
        </div>
      </section>
    </div>

    <!-- When -->
    <section class="n-card" data-testid="notif-events-card">
      <div class="n-card__head">
        <span class="n-card__icon"><i class="fa fa-bell" aria-hidden="true"></i></span>
        <div class="n-card__heading">
          <h2>When to notify</h2>
          <p>Pick the events each channel should hear about.</p>
        </div>
      </div>

      <div class="n-matrix" role="table" aria-label="Events per channel">
        <div class="n-matrix__row n-matrix__row--head" role="row">
          <span role="columnheader">Event</span>
          <span role="columnheader" class="n-matrix__col" [class.n-matrix__col--off]="!form.email_enabled">Email</span>
          <span role="columnheader" class="n-matrix__col"
            [class.n-matrix__col--off]="!telegramAvailable || !form.telegram_enabled">Telegram</span>
        </div>
        @for (event of events; track event.key) {
          <div class="n-matrix__row" role="row" [attr.data-testid]="'notif-event-' + event.key">
            <span class="n-event" role="cell">
              <i class="fa {{ event.icon }}" aria-hidden="true"></i>
              <span>
                <span class="n-event__label">{{ event.label }}</span>
                <span class="n-event__desc">{{ event.description }}</span>
              </span>
            </span>
            <span class="n-matrix__col" role="cell">
              <input type="checkbox" class="n-check" [checked]="isOn('email', event.key)"
                (change)="toggleEvent('email', event.key)" [disabled]="!form.email_enabled"
                [attr.aria-label]="event.label + ' by email'" data-testid="notif-event-email">
            </span>
            <span class="n-matrix__col" role="cell">
              <input type="checkbox" class="n-check" [checked]="isOn('telegram', event.key)"
                (change)="toggleEvent('telegram', event.key)" [disabled]="!telegramAvailable || !form.telegram_enabled"
                [attr.aria-label]="event.label + ' on Telegram'" data-testid="notif-event-telegram">
            </span>
          </div>
        }
      </div>
    </section>

    <!-- Thresholds -->
    <section class="n-card" data-testid="notif-thresholds-card">
      <div class="n-card__head">
        <span class="n-card__icon"><i class="fa fa-sliders" aria-hidden="true"></i></span>
        <div class="n-card__heading">
          <h2>Thresholds</h2>
          <p>Tune how sensitive alerts are, so they stay worth reading.</p>
        </div>
      </div>

      <div class="n-thresholds">
        <div class="n-field">
          <label for="failureThreshold">Monitor down after</label>
          <div class="n-input-unit">
            <input id="failureThreshold" type="number" min="1" max="20" [(ngModel)]="form.monitor_failure_threshold"
              data-testid="notif-failure-threshold">
            <span>failed checks in a row</span>
          </div>
        </div>

        <div class="n-field">
          <label for="spikeMinute">Error spike per minute</label>
          <div class="n-input-unit">
            <input id="spikeMinute" type="number" min="0" [(ngModel)]="form.spike_minute_threshold"
              data-testid="notif-spike-minute">
            <span>errors or more <em>(0 = off)</em></span>
          </div>
        </div>

        <div class="n-field">
          <label for="spikeHour">Error spike per hour</label>
          <div class="n-input-unit">
            <input id="spikeHour" type="number" min="0" [(ngModel)]="form.spike_hour_threshold"
              data-testid="notif-spike-hour">
            <span>errors or more <em>(0 = off)</em></span>
          </div>
        </div>

        <div class="n-field">
          <label for="spikeMultiplier">…and at least</label>
          <div class="n-input-unit">
            <input id="spikeMultiplier" type="number" min="1" max="100" step="0.5" [(ngModel)]="form.spike_multiplier"
              data-testid="notif-spike-multiplier">
            <span>× the usual rate (last 7 days)</span>
          </div>
        </div>

        <div class="n-field">
          <label for="spikeCooldown">Repeat a spike alert at most every</label>
          <div class="n-input-unit">
            <input id="spikeCooldown" type="number" min="5" max="1440" [(ngModel)]="form.spike_cooldown_minutes"
              data-testid="notif-spike-cooldown">
            <span>minutes</span>
          </div>
        </div>
      </div>
      <p class="n-hint n-thresholds__note">
        Only <b>error</b> and <b>fatal</b> events count toward spikes and new-error alerts. Warnings are tracked but stay quiet.
      </p>
    </section>
  }
</div>

@if (dirty) {
  <div class="n-savebar" data-testid="notif-savebar">
    <span>You have unsaved changes</span>
    <div class="n-savebar__actions">
      <button type="button" class="pw-btn pw-btn--ghost" (click)="discard()" [disabled]="saving"
        data-testid="notif-discard">Discard</button>
      <button type="button" class="pw-btn pw-btn--primary" (click)="save()" [disabled]="saving" data-testid="notif-save">
        @if (saving) { <i class="fa fa-spinner fa-spin" aria-hidden="true"></i> }
        Save changes
      </button>
    </div>
  </div>
}
